import React, { useState, useEffect } from 'react';
import { format, startOfWeek, addDays, isSameDay, parseISO, startOfMonth, endOfMonth, startOfWeek as startOfWeekFns, endOfWeek } from 'date-fns';
import Modal from '../components/Modal';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { useAuth } from '../context/AuthContext';
import authFetch from '../utils/api';
import './Appointments.css';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  const phoneNumber = value.replace(/\D/g, '');
  if (phoneNumber.length <= 3) return phoneNumber;
  else if (phoneNumber.length <= 6) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
  else return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

// Format 24-hour time to 12-hour format
const formatTime12Hour = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${minutes} ${ampm}`;
};

const EVENT_COLORS = [
  { value: '#C9A227', label: 'Gold' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#10B981', label: 'Green' },
  { value: '#EF4444', label: 'Red' },
  { value: '#8B5CF6', label: 'Purple' },
  { value: '#6B7280', label: 'Gray' }
];

const Appointments = () => {
  const { isManagerOrAdmin, isTech } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [techs, setTechs] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState({ packages: [], addons: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetailModal, setShowEventDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [appointmentWeather, setAppointmentWeather] = useState({});
  const [eventWeather, setEventWeather] = useState({});
  const [dayWeather, setDayWeather] = useState({});

  // Mobile view state
  const [mobileView, setMobileView] = useState('day'); // 'day', 'week', 'month'
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    event_date: format(new Date(), 'yyyy-MM-dd'),
    event_time: '',
    end_time: '',
    location_address: '',
    location_city: '',
    location_state: '',
    location_zip: '',
    color: '#C9A227',
    allDay: true
  });

  const [formData, setFormData] = useState({
    customer_id: '',
    vehicle_id: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    scheduled_time: '09:00',
    location_address: '',
    location_city: '',
    location_state: 'TX',
    location_zip: '',
    notes: '',
    services: [],
    vehicle_type: 'car',
    assigned_tech_id: ''
  });

  const [slotPicker, setSlotPicker] = useState(null); // { day, time } when choosing Appointment vs Event for a clicked slot

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingAppointments, setConflictingAppointments] = useState([]);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    addVehicle: false, vehicle_year: '', vehicle_make: '',
    vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
  });

  useEffect(() => {
    fetchData();
  }, [selectedDate, viewMode]);

  const fetchData = async () => {
    try {
      let startDate, endDate;
      if (viewMode === 'month') {
        const monthStart = startOfMonth(selectedDate);
        const monthEnd = endOfMonth(selectedDate);
        startDate = startOfWeekFns(monthStart, { weekStartsOn: 0 });
        endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });
      } else {
        startDate = startOfWeek(selectedDate, { weekStartsOn: 0 });
        endDate = addDays(startDate, 6);
      }
      const dateRange = `start_date=${format(startDate, 'yyyy-MM-dd')}&end_date=${format(endDate, 'yyyy-MM-dd')}`;

      const [aptsRes, eventsRes, custsRes, servicesRes, techsRes] = await Promise.all([
        authFetch(`/api/appointments?${dateRange}`),
        fetch(`/api/calendar-events?${dateRange}`),
        fetch('/api/customers'),
        fetch('/api/services/grouped'),
        authFetch('/api/users?role=tech')
      ]);

      const [aptsData, eventsData, custsData, servicesData] = await Promise.all([
        aptsRes.json(),
        eventsRes.json(),
        custsRes.json(),
        servicesRes.json()
      ]);

      setAppointments(aptsData);
      setCalendarEvents(eventsData);
      setCustomers(custsData);
      setServices(servicesData);

      if (techsRes.ok) {
        const techsData = await techsRes.json();
        setTechs(techsData);
      }

      // Fetch weather for appointments and events with location data
      fetchWeatherForAppointments(aptsData);
      fetchWeatherForEvents(eventsData);
      fetchWeatherForDays();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeatherForDays = async () => {
    try {
      const res = await fetch('/api/weather/days');
      if (res.ok) {
        const data = await res.json();
        setDayWeather(data);
      }
    } catch (error) {
      console.error('Error fetching day weather:', error);
    }
  };

  const fetchWeatherForAppointments = async (appts) => {
    const weatherData = {};

    // Fetch weather for each appointment (limit to avoid too many API calls)
    const aptsWithLocation = appts.filter(apt => apt.location_city || apt.location_zip);

    for (const apt of aptsWithLocation.slice(0, 20)) { // Limit to 20 appointments
      try {
        const response = await fetch('/api/weather/forecast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address: apt.location_address,
            city: apt.location_city,
            state: apt.location_state,
            zip: apt.location_zip,
            date: apt.scheduled_date,
            time: apt.scheduled_time
          })
        });

        if (response.ok) {
          const weather = await response.json();
          weatherData[apt.id] = weather;
        }
      } catch (error) {
        console.error(`Error fetching weather for appointment ${apt.id}:`, error);
      }
    }

    setAppointmentWeather(weatherData);
  };

  const fetchWeatherForEvents = async (events) => {
    const weatherData = {};
    const eventsWithLocation = events.filter(evt => evt.location_city || evt.location_zip);

    for (const evt of eventsWithLocation.slice(0, 20)) {
      try {
        const response = await fetch('/api/weather/forecast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address: evt.location_address,
            city: evt.location_city,
            state: evt.location_state,
            zip: evt.location_zip,
            date: evt.event_date,
            time: evt.event_time || '12:00'
          })
        });

        if (response.ok) {
          const weather = await response.json();
          weatherData[`event-${evt.id}`] = weather;
        }
      } catch (error) {
        console.error(`Error fetching weather for event ${evt.id}:`, error);
      }
    }

    setEventWeather(weatherData);
  };

  // Event handlers
  const resetEventForm = () => {
    setEventFormData({
      title: '',
      description: '',
      event_date: format(new Date(), 'yyyy-MM-dd'),
      event_time: '',
      end_time: '',
      location_address: '',
      location_city: '',
      location_state: '',
      location_zip: '',
      color: '#C9A227',
      allDay: true
    });
    setEditingEvent(null);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: eventFormData.title,
        description: eventFormData.description || null,
        event_date: eventFormData.event_date,
        event_time: eventFormData.allDay ? null : eventFormData.event_time,
        end_time: eventFormData.allDay ? null : (eventFormData.end_time || null),
        location_address: eventFormData.location_address || null,
        location_city: eventFormData.location_city || null,
        location_state: eventFormData.location_state || null,
        location_zip: eventFormData.location_zip || null,
        color: eventFormData.color
      };

      const url = editingEvent ? `/api/calendar-events/${editingEvent.id}` : '/api/calendar-events';
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setShowEventModal(false);
        resetEventForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventDetailModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormData({
      title: event.title,
      description: event.description || '',
      event_date: event.event_date,
      event_time: event.event_time || '',
      end_time: event.end_time || '',
      location_address: event.location_address || '',
      location_city: event.location_city || '',
      location_state: event.location_state || '',
      location_zip: event.location_zip || '',
      color: event.color || '#C9A227',
      allDay: !event.event_time
    });
    setShowEventDetailModal(false);
    setShowEventModal(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Move this event to trash?')) return;
    try {
      const response = await fetch(`/api/calendar-events/${eventId}`, { method: 'DELETE' });
      if (response.ok) {
        setShowEventDetailModal(false);
        setSelectedEvent(null);
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const getEventsForDay = (date) => {
    return calendarEvents.filter(evt =>
      isSameDay(parseISO(evt.event_date), date)
    );
  };

  const handleCustomerSelect = async (customerId) => {
    setFormData({ ...formData, customer_id: customerId, vehicle_id: '' });
    setCustomerAddresses([]);
    setSelectedAddressId('');

    if (!customerId) {
      setSelectedCustomer(null);
      return;
    }

    try {
      const [customerRes, addressRes] = await Promise.all([
        fetch(`/api/customers/${customerId}`),
        fetch(`/api/customers/${customerId}/addresses`)
      ]);
      const customer = await customerRes.json();
      const savedAddresses = await addressRes.json();
      setSelectedCustomer(customer);

      // Build combined list: profile address first (if exists), then saved addresses
      const combined = [];
      if (customer.address) {
        combined.push({ id: 'profile', name: 'Main Address', address: customer.address, city: customer.city, state: customer.state, zip: customer.zip });
      }
      combined.push(...savedAddresses);
      setCustomerAddresses(combined);

      // Auto-select: default saved address, or profile address, or nothing
      const defaultSaved = savedAddresses.find(a => a.is_default === 1);
      const autoSelect = defaultSaved || combined[0];

      if (autoSelect) {
        setSelectedAddressId(autoSelect.id.toString());
        setFormData(prev => ({
          ...prev,
          customer_id: customerId,
          location_address: autoSelect.address || '',
          location_city: autoSelect.city || '',
          location_state: autoSelect.state || 'TX',
          location_zip: autoSelect.zip || ''
        }));
      } else {
        setFormData(prev => ({ ...prev, customer_id: customerId }));
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.first_name || !newCustomerData.last_name || !newCustomerData.phone) {
      alert('Please fill in first name, last name, and phone');
      return;
    }
    if (newCustomerData.addVehicle && (!newCustomerData.vehicle_make || !newCustomerData.vehicle_model)) {
      alert('Please fill in vehicle make and model');
      return;
    }
    try {
      const response = await fetch('/api/customers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: newCustomerData.first_name,
          last_name: newCustomerData.last_name,
          phone: newCustomerData.phone,
          email: newCustomerData.email
        })
      });
      if (response.ok) {
        const newCustomer = await response.json();
        if (newCustomerData.addVehicle && newCustomerData.vehicle_make && newCustomerData.vehicle_model) {
          await fetch('/api/vehicles', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customer_id: newCustomer.id,
              year: newCustomerData.vehicle_year || null,
              make: newCustomerData.vehicle_make,
              model: newCustomerData.vehicle_model,
              color: newCustomerData.vehicle_color || null,
              vehicle_type: newCustomerData.vehicle_type
            })
          });
        }
        const customersRes = await fetch('/api/customers');
        const customersData = await customersRes.json();
        setCustomers(customersData);
        await handleCustomerSelect(newCustomer.id.toString());
        setShowNewCustomerForm(false);
        setNewCustomerData({
          first_name: '', last_name: '', phone: '', email: '',
          addVehicle: false, vehicle_year: '', vehicle_make: '',
          vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
        });
      }
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  const handleAddressSelect = (addressId) => {
    setSelectedAddressId(addressId);

    if (addressId === 'custom') {
      setFormData(prev => ({ ...prev, location_address: '', location_city: '', location_state: 'TX', location_zip: '' }));
      return;
    }

    const address = customerAddresses.find(a => a.id.toString() === addressId);
    if (address) {
      setFormData(prev => ({
        ...prev,
        location_address: address.address || '',
        location_city: address.city || '',
        location_state: address.state || 'TX',
        location_zip: address.zip || ''
      }));
    }
  };

  const handleServiceToggle = (service, price) => {
    const serviceId = service.id || service.service_id;
    const existing = formData.services.find(s => s.service_id === serviceId);
    if (existing) {
      setFormData({
        ...formData,
        services: formData.services.filter(s => s.service_id !== serviceId)
      });
    } else {
      setFormData({
        ...formData,
        services: [...formData.services, {
          service_id: serviceId,
          name: service.name,
          category: service.category,
          price
        }]
      });
    }
  };

  const updateServicePrice = (serviceId, newPrice) => {
    setFormData({
      ...formData,
      services: formData.services.map(s =>
        s.service_id === serviceId ? { ...s, price: parseFloat(newPrice) || 0 } : s
      )
    });
  };

  const handleVehicleTypeChange = (newVehicleType) => {
    // Update prices for all selected services based on new vehicle type
    const updatedServices = formData.services.map(selectedService => {
      const fullService = [...services.packages, ...services.addons].find(s => s.id === selectedService.service_id);
      if (fullService) {
        const newPrice = newVehicleType === 'truck'
          ? (fullService.base_price_truck || fullService.base_price_car)
          : fullService.base_price_car;
        return { ...selectedService, price: newPrice };
      }
      return selectedService;
    });

    setFormData({
      ...formData,
      vehicle_type: newVehicleType,
      services: updatedServices
    });
  };

  const fetchAppointmentDetail = async (id) => {
    try {
      const response = await fetch(`/api/appointments/${id}`);
      const data = await response.json();
      setSelectedAppointment(data);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleEditAppointment = async (appointment) => {
    try {
      const response = await fetch(`/api/appointments/${appointment.id}`);
      const data = await response.json();

      setEditingAppointment(data);

      // Load customer to get vehicles
      if (data.customer_id) {
        const customerResponse = await fetch(`/api/customers/${data.customer_id}`);
        const customerData = await customerResponse.json();
        setSelectedCustomer(customerData);
      }

      // Map services from API format (id, name, category, price) to form format (service_id, name, category, price)
      const mappedServices = (data.services || []).map(s => ({
        service_id: s.id,
        name: s.name,
        category: s.category || 'addon',
        price: s.price
      }));

      setFormData({
        customer_id: data.customer_id.toString(),
        vehicle_id: data.vehicle_id ? data.vehicle_id.toString() : '',
        scheduled_date: data.scheduled_date,
        scheduled_time: data.scheduled_time,
        location_address: data.location_address || '',
        location_city: data.location_city || '',
        location_state: data.location_state || '',
        location_zip: data.location_zip || '',
        notes: data.notes || '',
        services: mappedServices,
        vehicle_type: data.vehicle_type || 'car',
        assigned_tech_id: data.assigned_tech_id ? data.assigned_tech_id.toString() : ''
      });

      setShowDetailModal(false);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading appointment for edit:', error);
    }
  };

  const checkForConflicts = async () => {
    try {
      const excludeId = editingAppointment ? editingAppointment.id : null;
      const url = `/api/appointments/check-conflicts?date=${formData.scheduled_date}&time=${formData.scheduled_time}${excludeId ? `&exclude_id=${excludeId}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return { hasConflict: false, conflicts: [] };
    }
  };

  const submitAppointment = async () => {
    try {
      if (editingAppointment) {
        // UPDATE mode - PUT request
        const response = await authFetch(`/api/appointments/${editingAppointment.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          setShowModal(false);
          setEditingAppointment(null);
          resetForm();
          fetchData();
        }
      } else {
        // CREATE mode - POST request
        const response = await authFetch('/api/appointments', {
          method: 'POST',
          body: JSON.stringify(formData)
        });

        if (response.ok) {
          setShowModal(false);
          resetForm();
          fetchData();
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check for conflicts
    const { hasConflict, conflicts } = await checkForConflicts();

    if (hasConflict) {
      setConflictingAppointments(conflicts);
      setShowConflictModal(true);
    } else {
      submitAppointment();
    }
  };

  const handleConfirmConflict = () => {
    setShowConflictModal(false);
    setConflictingAppointments([]);
    submitAppointment();
  };

  const handleCancelConflict = () => {
    setShowConflictModal(false);
    setConflictingAppointments([]);
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Move this appointment to trash? You can restore it later from the Trash page.')) {
      return;
    }

    try {
      const response = await fetch(`/api/appointments/${appointmentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setShowDetailModal(false);
        setSelectedAppointment(null);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Failed to delete appointment: ${error.error}`);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment. Please try again.');
    }
  };

  const handleCalculateMileage = async (appointmentId) => {
    try {
      const response = await authFetch(`/api/mileage/calculate-from-appointment/${appointmentId}`, {
        method: 'POST'
      });

      const data = await response.json();

      if (response.ok) {
        alert(`Mileage calculated!\n\nTo appointment: ${data.toAppointment} miles\nReturn home: ${data.returnHome} miles\nTotal: ${data.totalMiles} miles`);
      } else {
        alert(`Failed to calculate mileage: ${data.error}`);
      }
    } catch (error) {
      console.error('Error calculating mileage:', error);
      alert('Failed to calculate mileage. Please try again.');
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await fetch(`/api/appointments/${appointmentId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchData();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleConvertToInvoice = async (appointment) => {
    try {
      if (!appointment.services || appointment.services.length === 0) {
        const proceed = window.confirm('This appointment has no services selected. Do you want to create an invoice anyway?');
        if (!proceed) return;
      }

      const response = await fetch(`/api/invoices/from-appointment/${appointment.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          due_date: appointment.scheduled_date,
          notes: appointment.notes
        })
      });

      if (response.ok) {
        const invoice = await response.json();
        alert(`Invoice ${invoice.invoice_number} created successfully!`);
        setShowDetailModal(false);
        fetchData(); // Refresh appointments to update any linked invoice data
      } else {
        const error = await response.json();
        alert(`Failed to create invoice: ${error.error}`);
      }
    } catch (error) {
      console.error('Error converting to invoice:', error);
      alert('Failed to create invoice. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      vehicle_id: '',
      scheduled_date: format(new Date(), 'yyyy-MM-dd'),
      scheduled_time: '09:00',
      location_address: '',
      location_city: '',
      location_state: 'TX',
      location_zip: '',
      notes: '',
      services: [],
      vehicle_type: 'car',
      assigned_tech_id: ''
    });
    setEditingAppointment(null);
    setSelectedCustomer(null);
    setCustomerAddresses([]);
    setSelectedAddressId('');
    setShowNewCustomerForm(false);
    setNewCustomerData({
      first_name: '', last_name: '', phone: '', email: '',
      addVehicle: false, vehicle_year: '', vehicle_make: '',
      vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
    });
  };

  const [draggedAptId, setDraggedAptId] = useState(null);

  const handleDrop = async (e, day, time) => {
    e.preventDefault();
    if (!draggedAptId) return;
    const newDate = format(day, 'yyyy-MM-dd');
    const newTime = time;
    setDraggedAptId(null);
    try {
      await authFetch(`/api/appointments/${draggedAptId}/reschedule`, {
        method: 'PATCH',
        body: JSON.stringify({ scheduled_date: newDate, scheduled_time: newTime })
      });
      fetchData();
    } catch (error) {
      console.error('Error rescheduling:', error);
    }
  };

  const handleEmptySlotClick = (day, time) => {
    setSlotPicker({ day, time });
  };

  const chooseAppointmentForSlot = () => {
    if (!slotPicker) return;
    setFormData(prev => ({
      ...prev,
      scheduled_date: format(slotPicker.day, 'yyyy-MM-dd'),
      scheduled_time: slotPicker.time
    }));
    setSlotPicker(null);
    setShowModal(true);
  };

  const chooseEventForSlot = () => {
    if (!slotPicker) return;
    setEventFormData(prev => ({
      ...prev,
      event_date: format(slotPicker.day, 'yyyy-MM-dd'),
      event_time: slotPicker.time,
      allDay: false
    }));
    setSlotPicker(null);
    setShowEventModal(true);
  };

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 0 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const getAppointmentsForDay = (date) => {
    return appointments.filter(apt =>
      isSameDay(parseISO(apt.scheduled_date), date)
    );
  };

  const timeSlots = [];
  for (let hour = 7; hour <= 19; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // The existing calendar grid JSX (used for desktop and mobile week view)
  const calendarGridJSX = (
    <div className="calendar-grid">
      <div className="calendar-header-row">
        <div className="calendar-time-header"></div>
        {weekDays.map((day, index) => (
          <div
            key={index}
            className={`calendar-day-header ${isSameDay(day, new Date()) ? 'calendar-day-header--today' : ''}`}
          >
            <span className="calendar-day-name">{format(day, 'EEE')}</span>
            <span className="calendar-day-number">{format(day, 'd')}</span>
            {dayWeather[format(day, 'yyyy-MM-dd')] && (
              <div className="calendar-day-weather">
                <img
                  src={`https://openweathermap.org/img/wn/${dayWeather[format(day, 'yyyy-MM-dd')].icon}.png`}
                  alt={dayWeather[format(day, 'yyyy-MM-dd')].description}
                  className="calendar-day-weather__icon"
                />
                <span className="calendar-day-weather__temp">{dayWeather[format(day, 'yyyy-MM-dd')].temp}°</span>
                {dayWeather[format(day, 'yyyy-MM-dd')].precipitation > 0 && (
                  <span className="calendar-day-weather__precip">{dayWeather[format(day, 'yyyy-MM-dd')].precipitation}%</span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* All-day events row */}
      {weekDays.some(day => getEventsForDay(day).some(e => !e.event_time)) && (
        <div className="calendar-allday-row">
          <div className="calendar-time" style={{ fontSize: '0.65rem' }}>ALL DAY</div>
          {weekDays.map((day, dayIndex) => {
            const allDayEvents = getEventsForDay(day).filter(e => !e.event_time);
            return (
              <div key={dayIndex} className="calendar-cell calendar-cell--allday">
                {allDayEvents.map((evt) => (
                  <div
                    key={`event-${evt.id}`}
                    className="event-card event-card--clickable"
                    style={{ borderLeftColor: evt.color || '#C9A227' }}
                    onClick={() => handleEventClick(evt)}
                  >
                    <div className="event-card__title">{evt.title}</div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <div className="calendar-body">
        {timeSlots.map((time) => (
          <div key={time} className="calendar-row">
            <div className="calendar-time">{time}</div>
            {weekDays.map((day, dayIndex) => {
              const dayAppointments = getAppointmentsForDay(day).filter(
                apt => apt.scheduled_time.startsWith(time.split(':')[0])
              );
              const dayEvents = getEventsForDay(day).filter(
                evt => evt.event_time && evt.event_time.startsWith(time.split(':')[0])
              );

              return (
                <div
                  key={dayIndex}
                  className={`calendar-cell${draggedAptId ? ' calendar-cell--droptarget' : ''}`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, day, time)}
                  onClick={(e) => { if (e.target === e.currentTarget) handleEmptySlotClick(day, time); }}
                >
                  {dayEvents.map((evt) => (
                    <div
                      key={`event-${evt.id}`}
                      className="event-card event-card--clickable"
                      style={{ borderLeftColor: evt.color || '#C9A227' }}
                      onClick={() => handleEventClick(evt)}
                    >
                      <div className="event-card__time">
                        {formatTime12Hour(evt.event_time)}
                      </div>
                      <div className="event-card__title">{evt.title}</div>
                      {eventWeather[`event-${evt.id}`] && eventWeather[`event-${evt.id}`].temp && (
                        <div className="appointment-card__weather">
                          <img
                            src={`https://openweathermap.org/img/wn/${eventWeather[`event-${evt.id}`].icon}.png`}
                            alt={eventWeather[`event-${evt.id}`].description}
                            className="appointment-card__weather-icon"
                          />
                          <div className="appointment-card__weather-info">
                            <span className="appointment-card__temp">{eventWeather[`event-${evt.id}`].temp}°F</span>
                            <span className="appointment-card__precip">{eventWeather[`event-${evt.id}`].precipitation}%</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {dayAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className={`appointment-card appointment-card--${apt.status} appointment-card--clickable${draggedAptId === apt.id ? ' appointment-card--dragging' : ''}`}
                      onClick={() => fetchAppointmentDetail(apt.id)}
                      draggable={!isMobile}
                      onDragStart={() => setDraggedAptId(apt.id)}
                      onDragEnd={() => setDraggedAptId(null)}
                    >
                      <div className="appointment-card__time">
                        {formatTime12Hour(apt.scheduled_time)}
                      </div>
                      <div className="appointment-card__customer">
                        {apt.first_name} {apt.last_name}
                      </div>
                      <div className="appointment-card__vehicle">
                        {apt.year} {apt.make} {apt.model}
                      </div>
                      {appointmentWeather[apt.id] && appointmentWeather[apt.id].temp && (
                        <div className="appointment-card__weather">
                          <img
                            src={`https://openweathermap.org/img/wn/${appointmentWeather[apt.id].icon}.png`}
                            alt={appointmentWeather[apt.id].description}
                            className="appointment-card__weather-icon"
                          />
                          <div className="appointment-card__weather-info">
                            <span className="appointment-card__temp">{appointmentWeather[apt.id].temp}°F</span>
                            <span className="appointment-card__precip">{appointmentWeather[apt.id].precipitation}%</span>
                          </div>
                        </div>
                      )}
                      <div className="appointment-card__actions" onClick={(e) => e.stopPropagation()}>
                        <select
                          className="appointment-status-select"
                          value={apt.status}
                          onChange={(e) => handleStatusUpdate(apt.id, e.target.value)}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="no_show">No Show</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  // Desktop Month View JSX
  const desktopMonthViewJSX = (() => {
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);
    const gridStart = startOfWeekFns(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const days = [];
    let d = new Date(gridStart);
    while (d <= gridEnd) { days.push(new Date(d)); d = addDays(d, 1); }

    return (
      <div className="desktop-month-view">
        <div className="desktop-month-header">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(dow => (
            <div key={dow} className="desktop-month-dow">{dow}</div>
          ))}
        </div>
        <div className="desktop-month-grid">
          {days.map(day => {
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
            const isToday = isSameDay(day, new Date());
            const dayAppts = getAppointmentsForDay(day);
            const dayEvents = getEventsForDay(day);
            const allItems = [
              ...dayAppts.map(a => ({ type: 'apt', ...a })),
              ...dayEvents.map(e => ({ type: 'evt', ...e }))
            ].sort((a, b) => {
              const aTime = a.type === 'apt' ? (a.scheduled_time || '') : (a.event_time || '');
              const bTime = b.type === 'apt' ? (b.scheduled_time || '') : (b.event_time || '');
              return aTime.localeCompare(bTime);
            });
            const visible = allItems.slice(0, 3);
            const overflow = allItems.length - 3;

            return (
              <div
                key={day.toISOString()}
                className={['desktop-month-cell', !isCurrentMonth ? 'desktop-month-cell--other' : '', isToday ? 'desktop-month-cell--today' : ''].filter(Boolean).join(' ')}
                onClick={(e) => { if (e.target === e.currentTarget) handleEmptySlotClick(day, '09:00'); }}
              >
                <div className="desktop-month-cell__top">
                  <div
                    className={`desktop-month-cell__num${isToday ? ' desktop-month-cell__num--today' : ''}`}
                    title="View week"
                    onClick={(e) => { e.stopPropagation(); setSelectedDate(day); setViewMode('week'); }}
                  >
                    {format(day, 'd')}
                  </div>
                  {dayWeather[format(day, 'yyyy-MM-dd')] && (
                    <div className="desktop-month-cell__weather">
                      <img
                        src={`https://openweathermap.org/img/wn/${dayWeather[format(day, 'yyyy-MM-dd')].icon}.png`}
                        alt={dayWeather[format(day, 'yyyy-MM-dd')].description}
                        className="desktop-month-cell__weather-icon"
                      />
                      <span className="desktop-month-cell__weather-temp">{dayWeather[format(day, 'yyyy-MM-dd')].temp}°</span>
                      {dayWeather[format(day, 'yyyy-MM-dd')].precipitation > 0 && (
                        <span className="desktop-month-cell__weather-precip">{dayWeather[format(day, 'yyyy-MM-dd')].precipitation}%</span>
                      )}
                    </div>
                  )}
                </div>
                {visible.map(item =>
                  item.type === 'apt' ? (
                    <div
                      key={`apt-${item.id}`}
                      className={`desktop-month-apt desktop-month-apt--${item.status}`}
                      onClick={(e) => { e.stopPropagation(); fetchAppointmentDetail(item.id); }}
                    >
                      <span className="desktop-month-apt__time">{formatTime12Hour(item.scheduled_time)}</span>
                      <span className="desktop-month-apt__name">{item.first_name} {item.last_name}</span>
                    </div>
                  ) : (
                    <div
                      key={`evt-${item.id}`}
                      className="desktop-month-apt desktop-month-apt--event"
                      style={{ borderLeftColor: item.color || '#C9A227' }}
                      onClick={(e) => { e.stopPropagation(); handleEventClick(item); }}
                    >
                      <span className="desktop-month-apt__name">{item.title}</span>
                    </div>
                  )
                )}
                {overflow > 0 && (
                  <div className="desktop-month-overflow" onClick={(e) => { e.stopPropagation(); setSelectedDate(day); setViewMode('week'); }}>+{overflow} more</div>
                )}
                <div className="desktop-month-cell__add" onClick={() => handleEmptySlotClick(day, '09:00')}>+</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  })();

  // Mobile Day View JSX
  const mobileDayViewJSX = (
    <div className="mobile-day-view">
      {/* Date navigation */}
      <div className="mobile-day-nav">
        <button className="mobile-day-nav__btn" onClick={() => setSelectedDate(d => addDays(d, -1))}>&#x2039;</button>
        <span className="mobile-day-nav__date">
          {format(selectedDate, 'EEEE, MMMM d')}
          {isSameDay(selectedDate, new Date()) && <span className="mobile-day-nav__today"> &middot; Today</span>}
        </span>
        <button className="mobile-day-nav__btn" onClick={() => setSelectedDate(d => addDays(d, 1))}>&#x203A;</button>
      </div>

      {/* Appointments for the day */}
      {(() => {
        const dayAppts = appointments.filter(a => isSameDay(parseISO(a.scheduled_date), selectedDate));
        const dayEvents = calendarEvents.filter(e => isSameDay(parseISO(e.event_date), selectedDate));
        const allItems = [
          ...dayAppts.map(a => ({ type: 'appointment', ...a })),
          ...dayEvents.map(e => ({ type: 'event', ...e }))
        ].sort((a, b) => {
          const aTime = a.type === 'appointment' ? (a.scheduled_time || '') : (a.event_time || '');
          const bTime = b.type === 'appointment' ? (b.scheduled_time || '') : (b.event_time || '');
          return aTime.localeCompare(bTime);
        });

        if (allItems.length === 0) {
          return <div className="mobile-day-empty">No appointments for this day</div>;
        }

        return allItems.map(item => {
          if (item.type === 'appointment') {
            return (
              <div
                key={item.id}
                className={`mobile-day-card mobile-day-card--${item.status}`}
                onClick={() => fetchAppointmentDetail(item.id)}
              >
                <div className="mobile-day-card__time">{formatTime12Hour(item.scheduled_time)}</div>
                <div className="mobile-day-card__info">
                  <div className="mobile-day-card__customer">{item.first_name} {item.last_name}</div>
                  <div className="mobile-day-card__vehicle">{item.year} {item.make} {item.model}</div>
                  <div className="mobile-day-card__services">{Array.isArray(item.services) ? item.services.map(s => s.name).join(', ') : ''}</div>
                </div>
                <span className={`badge badge--${item.status}`}>{item.status?.replace('_', ' ')}</span>
              </div>
            );
          } else {
            return (
              <div
                key={`event-${item.id}`}
                className="mobile-day-card mobile-day-card--event"
                style={{ borderLeftColor: item.color || '#C9A227' }}
                onClick={() => { setSelectedEvent(item); setShowEventDetailModal(true); }}
              >
                <div className="mobile-day-card__time">{item.all_day || !item.event_time ? 'All day' : formatTime12Hour(item.event_time)}</div>
                <div className="mobile-day-card__info">
                  <div className="mobile-day-card__customer">{item.title}</div>
                  {item.description && <div className="mobile-day-card__vehicle">{item.description}</div>}
                </div>
              </div>
            );
          }
        });
      })()}
    </div>
  );

  // Mobile Month View JSX
  const mobileMonthViewJSX = (
    <div className="mobile-month-view">
      {/* Month navigation */}
      <div className="mobile-day-nav">
        <button className="mobile-day-nav__btn" onClick={() => setSelectedDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}>&#x2039;</button>
        <span className="mobile-day-nav__date">{format(selectedDate, 'MMMM yyyy')}</span>
        <button className="mobile-day-nav__btn" onClick={() => setSelectedDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}>&#x203A;</button>
      </div>

      {/* Day-of-week headers */}
      <div className="mobile-month-grid">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="mobile-month-dow">{d}</div>
        ))}

        {/* Calendar days */}
        {(() => {
          const monthStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
          const monthEnd = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
          const gridStart = startOfWeekFns(monthStart, { weekStartsOn: 0 });
          const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });

          const days = [];
          let d = new Date(gridStart);
          while (d <= gridEnd) {
            const day = new Date(d);
            const isCurrentMonth = day.getMonth() === selectedDate.getMonth();
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            const hasAppts = appointments.some(a => isSameDay(parseISO(a.scheduled_date), day));
            const hasEvents = calendarEvents.some(e => isSameDay(parseISO(e.event_date), day));

            days.push(
              <div
                key={day.toISOString()}
                className={[
                  'mobile-month-day',
                  !isCurrentMonth ? 'mobile-month-day--other' : '',
                  isToday ? 'mobile-month-day--today' : '',
                  isSelected ? 'mobile-month-day--selected' : '',
                ].join(' ')}
                onClick={() => { setSelectedDate(day); setMobileView('day'); }}
              >
                <span className="mobile-month-day__num">{format(day, 'd')}</span>
                {(hasAppts || hasEvents) && <span className="mobile-month-day__dot" />}
              </div>
            );
            d = addDays(d, 1);
          }
          return days;
        })()}
      </div>

      {/* List of appointments for selected day below the grid */}
      <div className="mobile-month-appts">
        {(() => {
          const dayAppts = appointments.filter(a => isSameDay(parseISO(a.scheduled_date), selectedDate));
          if (dayAppts.length === 0) return <p className="mobile-day-empty">Tap a day to see appointments</p>;
          return dayAppts.map(a => (
            <div
              key={a.id}
              className={`mobile-day-card mobile-day-card--${a.status}`}
              onClick={() => fetchAppointmentDetail(a.id)}
            >
              <div className="mobile-day-card__time">{formatTime12Hour(a.scheduled_time)}</div>
              <div className="mobile-day-card__info">
                <div className="mobile-day-card__customer">{a.first_name} {a.last_name}</div>
                <div className="mobile-day-card__vehicle">{a.year} {a.make} {a.model}</div>
              </div>
              <span className={`badge badge--${a.status}`}>{a.status?.replace('_', ' ')}</span>
            </div>
          ));
        })()}
      </div>
    </div>
  );

  return (
    <div className="appointments-page">
      <div className="page-header">
        <h1 className="page-header__title">Schedule</h1>
        <div className="page-header__actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn--outline" onClick={() => setShowEventModal(true)}>
            + Event
          </button>
          <button className="btn btn--primary" onClick={() => setShowModal(true)}>
            + Appointment
          </button>
        </div>
      </div>

      {/* Mobile View Switcher */}
      {isMobile && (
        <div className="mobile-view-switcher">
          <button
            className={`mobile-view-btn ${mobileView === 'day' ? 'mobile-view-btn--active' : ''}`}
            onClick={() => setMobileView('day')}
          >Day</button>
          <button
            className={`mobile-view-btn ${mobileView === 'week' ? 'mobile-view-btn--active' : ''}`}
            onClick={() => setMobileView('week')}
          >Week</button>
          <button
            className={`mobile-view-btn ${mobileView === 'month' ? 'mobile-view-btn--active' : ''}`}
            onClick={() => setMobileView('month')}
          >Month</button>
        </div>
      )}

      <div className="calendar-header">
        <button
          className="btn btn--secondary"
          onClick={() => viewMode === 'month'
            ? setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))
            : setSelectedDate(addDays(selectedDate, -7))
          }
        >
          &larr; {viewMode === 'month' ? 'Prev Month' : 'Prev Week'}
        </button>
        <div className="calendar-header__center">
          <h2 className="calendar-title">
            {viewMode === 'month'
              ? format(selectedDate, 'MMMM yyyy')
              : `${format(weekStart, 'MMMM d')} – ${format(addDays(weekStart, 6), 'MMMM d, yyyy')}`
            }
          </h2>
          <div className="desktop-view-switcher">
            <button
              className={`desktop-view-btn${viewMode === 'week' ? ' desktop-view-btn--active' : ''}`}
              onClick={() => setViewMode('week')}
            >Week</button>
            <button
              className={`desktop-view-btn${viewMode === 'month' ? ' desktop-view-btn--active' : ''}`}
              onClick={() => setViewMode('month')}
            >Month</button>
          </div>
        </div>
        <button
          className="btn btn--secondary"
          onClick={() => viewMode === 'month'
            ? setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))
            : setSelectedDate(addDays(selectedDate, 7))
          }
        >
          {viewMode === 'month' ? 'Next Month' : 'Next Week'} &rarr;
        </button>
      </div>

      {/* Conditionally render the right view */}
      {isMobile && mobileView === 'day' ? (
        mobileDayViewJSX
      ) : viewMode === 'month' ? (
        isMobile ? mobileMonthViewJSX : desktopMonthViewJSX
      ) : (
        calendarGridJSX
      )}

      <Modal
        isOpen={!!slotPicker}
        onClose={() => setSlotPicker(null)}
        title="New Appointment or Event?"
      >
        {slotPicker && (
          <p className="slot-picker-hint">
            {format(slotPicker.day, 'EEEE, MMM d')} at {formatTime12Hour(slotPicker.time)}
          </p>
        )}
        <div className="slot-picker-actions">
          <button type="button" className="btn btn--primary" onClick={chooseAppointmentForSlot}>
            Appointment
          </button>
          <button type="button" className="btn btn--outline" onClick={chooseEventForSlot}>
            Event
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingAppointment ? "Edit Appointment" : "New Appointment"}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Customer *</label>
            {!showNewCustomerForm ? (
              <div className="customer-select-wrapper">
                <CustomerSearchSelect
                  customers={customers}
                  value={formData.customer_id}
                  onChange={(id) => handleCustomerSelect(id)}
                  disabled={!!editingAppointment}
                />
                {!editingAppointment && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small new-customer-btn"
                    onClick={() => setShowNewCustomerForm(true)}
                  >
                    + New Customer
                  </button>
                )}
              </div>
            ) : (
              <div className="new-customer-form">
                <div className="form-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="First Name *"
                    value={newCustomerData.first_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, first_name: e.target.value })}
                  />
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Last Name *"
                    value={newCustomerData.last_name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, last_name: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="Phone * (XXX-XXX-XXXX)"
                    value={newCustomerData.phone}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: formatPhoneNumber(e.target.value) })}
                  />
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Email (optional)"
                    value={newCustomerData.email}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  />
                </div>

                <div className="add-vehicle-toggle">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newCustomerData.addVehicle}
                      onChange={(e) => setNewCustomerData({ ...newCustomerData, addVehicle: e.target.checked })}
                    />
                    <span>Add a vehicle for this customer</span>
                  </label>
                </div>

                {newCustomerData.addVehicle && (
                  <div className="vehicle-fields">
                    <div className="form-row form-row--3">
                      <input
                        type="number"
                        className="form-input"
                        placeholder="Year"
                        value={newCustomerData.vehicle_year}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_year: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Make *"
                        value={newCustomerData.vehicle_make}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_make: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Model *"
                        value={newCustomerData.vehicle_model}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_model: e.target.value })}
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Color"
                        value={newCustomerData.vehicle_color}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_color: e.target.value })}
                      />
                      <select
                        className="form-select"
                        value={newCustomerData.vehicle_type}
                        onChange={(e) => setNewCustomerData({ ...newCustomerData, vehicle_type: e.target.value })}
                      >
                        <option value="sedan">Sedan</option>
                        <option value="coupe">Coupe</option>
                        <option value="hatchback">Hatchback</option>
                        <option value="truck">Truck</option>
                        <option value="suv">SUV</option>
                        <option value="van">Van</option>
                        <option value="motorcycle">Motorcycle</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="new-customer-actions">
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => {
                      setShowNewCustomerForm(false);
                      setNewCustomerData({
                        first_name: '', last_name: '', phone: '', email: '',
                        addVehicle: false, vehicle_year: '', vehicle_make: '',
                        vehicle_model: '', vehicle_color: '', vehicle_type: 'sedan'
                      });
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn--primary btn--small"
                    onClick={handleCreateCustomer}
                  >
                    Add Customer
                  </button>
                </div>
              </div>
            )}
          </div>

          {selectedCustomer?.vehicles?.length > 0 && (
            <div className="form-group">
              <label className="form-label">Vehicle</label>
              <select
                className="form-select"
                value={formData.vehicle_id}
                onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
              >
                <option value="">Select a vehicle</option>
                {selectedCustomer.vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model} {v.color ? `(${v.color})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.scheduled_date}
                onInput={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Time *</label>
              <input
                type="time"
                className="form-input"
                value={formData.scheduled_time}
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Vehicle Type Toggle */}
          <div className="form-group">
            <label className="form-label">Vehicle Type</label>
            <div className="vehicle-type-selector">
              <button
                type="button"
                className={`vehicle-type-btn ${formData.vehicle_type === 'car' ? 'vehicle-type-btn--active' : ''}`}
                onClick={() => handleVehicleTypeChange('car')}
              >
                Car
              </button>
              <button
                type="button"
                className={`vehicle-type-btn ${formData.vehicle_type === 'truck' ? 'vehicle-type-btn--active' : ''}`}
                onClick={() => handleVehicleTypeChange('truck')}
              >
                SUV / Truck
              </button>
              <button
                type="button"
                className={`vehicle-type-btn ${formData.vehicle_type !== 'car' && formData.vehicle_type !== 'truck' ? 'vehicle-type-btn--active' : ''}`}
                onClick={() => handleVehicleTypeChange('other')}
              >
                Other
              </button>
            </div>
            {formData.vehicle_type !== 'car' && formData.vehicle_type !== 'truck' && (
              <input
                type="text"
                className="form-input vehicle-type-input"
                placeholder="Enter vehicle type (e.g., Motorcycle, RV, Boat)"
                value={formData.vehicle_type === 'other' ? '' : formData.vehicle_type}
                onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value || 'other' })}
              />
            )}
          </div>

          {/* Packages Section */}
          <div className="form-group">
            <label className="form-label">Packages</label>
            <div className="service-selector">
              {services.packages.map((service) => {
                const isSelected = formData.services.some(s => s.service_id === service.id);
                const price = formData.vehicle_type === 'truck' ? service.base_price_truck : service.base_price_car;

                return (
                  <div
                    key={service.id}
                    className={`service-card ${isSelected ? 'service-card--selected' : ''}`}
                    onClick={() => handleServiceToggle(service, price)}
                  >
                    <div className="service-card__header">
                      <span className="service-card__name">{service.name}</span>
                      {service.base_price_truck && service.base_price_truck !== service.base_price_car ? (
                        <div className="service-card__pricing">
                          <span className={`service-card__price ${formData.vehicle_type === 'car' ? 'service-card__price--active' : ''}`}>
                            ${service.base_price_car}
                          </span>
                          <span className="service-card__price-separator">/</span>
                          <span className={`service-card__price ${formData.vehicle_type === 'truck' ? 'service-card__price--active' : ''}`}>
                            ${service.base_price_truck}
                          </span>
                        </div>
                      ) : (
                        <span className="service-card__price service-card__price--active">${service.base_price_car}</span>
                      )}
                    </div>
                    {service.description && (
                      <p className="service-card__desc">{service.description}</p>
                    )}
                    {isSelected && (
                      <div className="service-card__check">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Add-ons Section */}
          <div className="form-group">
            <label className="form-label">Add-ons</label>
            <div className="service-selector service-selector--addons">
              {services.addons.map((service) => {
                const isSelected = formData.services.some(s => s.service_id === service.id);
                const price = formData.vehicle_type === 'truck' ? (service.base_price_truck || service.base_price_car) : service.base_price_car;

                return (
                  <div
                    key={service.id}
                    className={`addon-chip ${isSelected ? 'addon-chip--selected' : ''}`}
                    onClick={() => handleServiceToggle(service, price)}
                  >
                    <span className="addon-chip__name">{service.name}</span>
                    <span className="addon-chip__price">+${price}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selected Services Summary */}
          {formData.services.length > 0 && (
            <div className="selected-services">
              <label className="form-label">Selected Items</label>
              {formData.services.map((service) => (
                <div key={service.service_id} className="selected-service-row">
                  <span className="selected-service-name">
                    {service.category === 'addon' && '+ '}
                    {service.name}
                  </span>
                  <div className="selected-service-price">
                    <span>$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={service.price}
                      onChange={(e) => updateServicePrice(service.service_id, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="price-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn--danger btn--icon"
                    onClick={() => handleServiceToggle(service, service.price)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Service Location</label>

            {customerAddresses.length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <select
                  className="form-select"
                  value={selectedAddressId}
                  onChange={(e) => handleAddressSelect(e.target.value)}
                >
                  {customerAddresses.map((addr) => (
                    <option key={addr.id} value={addr.id}>
                      {addr.name} - {addr.address}{addr.city && `, ${addr.city}`}
                    </option>
                  ))}
                  <option value="custom">+ Enter a different address</option>
                </select>
              </div>
            )}

            <input
              type="text"
              className="form-input"
              placeholder="Street Address"
              value={formData.location_address}
              onChange={(e) => {
                setFormData({ ...formData, location_address: e.target.value });
                if (customerAddresses.length > 0) setSelectedAddressId('custom');
              }}
            />
            <div className="form-row" style={{ marginTop: '0.5rem' }}>
              <input
                type="text"
                className="form-input"
                placeholder="City"
                value={formData.location_city}
                onChange={(e) => {
                  setFormData({ ...formData, location_city: e.target.value });
                  if (customerAddresses.length > 0) setSelectedAddressId('custom');
                }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="State"
                value={formData.location_state}
                onChange={(e) => {
                  setFormData({ ...formData, location_state: e.target.value });
                  if (customerAddresses.length > 0) setSelectedAddressId('custom');
                }}
                maxLength="2"
                style={{ width: '80px' }}
              />
              <input
                type="text"
                className="form-input"
                placeholder="ZIP"
                value={formData.location_zip}
                onChange={(e) => {
                  setFormData({ ...formData, location_zip: e.target.value });
                  if (customerAddresses.length > 0) setSelectedAddressId('custom');
                }}
                maxLength="5"
                style={{ width: '100px' }}
              />
            </div>
            <small className="form-hint">
              {customerAddresses.length > 0
                ? 'Select a saved address or enter a different one'
                : 'Enter the service location address'}
            </small>
          </div>

          {isManagerOrAdmin() && techs.length > 0 && (
            <div className="form-group">
              <label className="form-label">Assign To Tech</label>
              <select
                className="form-select"
                value={formData.assigned_tech_id}
                onChange={(e) => setFormData({ ...formData, assigned_tech_id: e.target.value })}
              >
                <option value="">Unassigned</option>
                {techs.map(tech => (
                  <option key={tech.id} value={tech.id}>{tech.username}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {editingAppointment ? 'Update Appointment' : 'Create Appointment'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Appointment Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Appointment Details"
      >
        {selectedAppointment && (
          <div className="appointment-detail">
            <div className="appointment-detail__section">
              <h3 className="appointment-detail__heading">Customer Information</h3>
              <p className="appointment-detail__text">
                <strong>{selectedAppointment.first_name} {selectedAppointment.last_name}</strong>
              </p>
              {selectedAppointment.phone && (
                <p className="appointment-detail__text">Phone: {selectedAppointment.phone}</p>
              )}
              {selectedAppointment.email && (
                <p className="appointment-detail__text">Email: {selectedAppointment.email}</p>
              )}
            </div>

            {selectedAppointment.vehicle_id && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Vehicle</h3>
                <p className="appointment-detail__text">
                  {selectedAppointment.year} {selectedAppointment.make} {selectedAppointment.model}
                  {selectedAppointment.color && ` (${selectedAppointment.color})`}
                </p>
              </div>
            )}

            <div className="appointment-detail__section">
              <h3 className="appointment-detail__heading">Appointment Time</h3>
              <p className="appointment-detail__text">
                {format(parseISO(selectedAppointment.scheduled_date), 'MMMM d, yyyy')} at {formatTime12Hour(selectedAppointment.scheduled_time)}
              </p>
              <p className="appointment-detail__text">
                <span className={`badge badge--${selectedAppointment.status}`}>
                  {selectedAppointment.status}
                </span>
              </p>
            </div>

            {appointmentWeather[selectedAppointment.id] && appointmentWeather[selectedAppointment.id].temp && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Weather Forecast</h3>
                <div className="appointment-detail__weather">
                  <img
                    src={`https://openweathermap.org/img/wn/${appointmentWeather[selectedAppointment.id].icon}@2x.png`}
                    alt={appointmentWeather[selectedAppointment.id].description}
                    className="appointment-detail__weather-icon"
                  />
                  <div className="appointment-detail__weather-info">
                    <p className="appointment-detail__text">
                      <strong>{appointmentWeather[selectedAppointment.id].temp}°F</strong> - {appointmentWeather[selectedAppointment.id].description}
                    </p>
                    <p className="appointment-detail__text">
                      High: {appointmentWeather[selectedAppointment.id].temp_max}°F | Low: {appointmentWeather[selectedAppointment.id].temp_min}°F
                    </p>
                    <p className="appointment-detail__text">
                      Precipitation: {appointmentWeather[selectedAppointment.id].precipitation}% | Wind: {appointmentWeather[selectedAppointment.id].windSpeed} mph
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedAppointment.services && selectedAppointment.services.length > 0 && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Services</h3>
                {selectedAppointment.services.map((service, index) => (
                  <p key={index} className="appointment-detail__text">
                    {service.name} - ${parseFloat(service.price).toFixed(2)}
                  </p>
                ))}
              </div>
            )}

            {selectedAppointment.location_address && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Service Location</h3>
                <p className="appointment-detail__text">{selectedAppointment.location_address}</p>
                {selectedAppointment.location_city && (
                  <p className="appointment-detail__text">
                    {selectedAppointment.location_city}, {selectedAppointment.location_state} {selectedAppointment.location_zip}
                  </p>
                )}
              </div>
            )}

            {selectedAppointment.assigned_tech_name && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Assigned Tech</h3>
                <p className="appointment-detail__text">{selectedAppointment.assigned_tech_name}</p>
              </div>
            )}

            {selectedAppointment.notes && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Notes</h3>
                <p className="appointment-detail__text">{selectedAppointment.notes}</p>
              </div>
            )}

            <div className="modal__footer">
              {!isTech() && (
                <button
                  className="btn btn--danger"
                  onClick={() => handleDeleteAppointment(selectedAppointment.id)}
                >
                  Delete
                </button>
              )}
              <button
                className="btn btn--outline"
                onClick={() => handleEditAppointment(selectedAppointment)}
              >
                Edit Appointment
              </button>
              <button
                className="btn btn--primary"
                onClick={() => handleConvertToInvoice(selectedAppointment)}
              >
                Convert to Invoice
              </button>
              <button
                className="btn btn--outline"
                onClick={() => handleCalculateMileage(selectedAppointment.id)}
              >
                Calculate Mileage
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Event Add/Edit Modal */}
      <Modal
        isOpen={showEventModal}
        onClose={() => { setShowEventModal(false); resetEventForm(); }}
        title={editingEvent ? "Edit Event" : "New Event"}
      >
        <form onSubmit={handleEventSubmit}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Event title"
              value={eventFormData.title}
              onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Date *</label>
            <input
              type="date"
              className="form-input"
              value={eventFormData.event_date}
              onChange={(e) => setEventFormData({ ...eventFormData, event_date: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={eventFormData.allDay}
                onChange={(e) => setEventFormData({ ...eventFormData, allDay: e.target.checked, event_time: '', end_time: '' })}
              />
              All Day
            </label>
          </div>

          {!eventFormData.allDay && (
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Start Time</label>
                <input
                  type="time"
                  className="form-input"
                  value={eventFormData.event_time}
                  onChange={(e) => setEventFormData({ ...eventFormData, event_time: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">End Time</label>
                <input
                  type="time"
                  className="form-input"
                  value={eventFormData.end_time}
                  onChange={(e) => setEventFormData({ ...eventFormData, end_time: e.target.value })}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Color</label>
            <div className="color-picker">
              {EVENT_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  className={`color-swatch ${eventFormData.color === c.value ? 'color-swatch--selected' : ''}`}
                  style={{ backgroundColor: c.value }}
                  onClick={() => setEventFormData({ ...eventFormData, color: c.value })}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Location</label>
            <input
              type="text"
              className="form-input"
              placeholder="Address"
              value={eventFormData.location_address}
              onChange={(e) => setEventFormData({ ...eventFormData, location_address: e.target.value })}
            />
          </div>

          <div className="form-row form-row--3">
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="City"
                value={eventFormData.location_city}
                onChange={(e) => setEventFormData({ ...eventFormData, location_city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="State"
                value={eventFormData.location_state}
                onChange={(e) => setEventFormData({ ...eventFormData, location_state: e.target.value })}
              />
            </div>
            <div className="form-group">
              <input
                type="text"
                className="form-input"
                placeholder="ZIP"
                value={eventFormData.location_zip}
                onChange={(e) => setEventFormData({ ...eventFormData, location_zip: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={eventFormData.description}
              onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
              rows="3"
              placeholder="Optional notes or details"
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowEventModal(false); resetEventForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">
              {editingEvent ? 'Update Event' : 'Create Event'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Event Detail Modal */}
      <Modal
        isOpen={showEventDetailModal}
        onClose={() => setShowEventDetailModal(false)}
        title="Event Details"
      >
        {selectedEvent && (
          <div className="appointment-detail">
            <div className="appointment-detail__section">
              <h3 className="appointment-detail__heading" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: selectedEvent.color || '#C9A227', display: 'inline-block' }}></span>
                {selectedEvent.title}
              </h3>
            </div>

            <div className="appointment-detail__section">
              <h3 className="appointment-detail__heading">Date & Time</h3>
              <p className="appointment-detail__text">
                {format(parseISO(selectedEvent.event_date), 'MMMM d, yyyy')}
                {selectedEvent.event_time && ` at ${formatTime12Hour(selectedEvent.event_time)}`}
                {selectedEvent.end_time && ` - ${formatTime12Hour(selectedEvent.end_time)}`}
                {!selectedEvent.event_time && ' (All Day)'}
              </p>
            </div>

            {eventWeather[`event-${selectedEvent.id}`] && eventWeather[`event-${selectedEvent.id}`].temp && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Weather Forecast</h3>
                <div className="appointment-detail__weather">
                  <img
                    src={`https://openweathermap.org/img/wn/${eventWeather[`event-${selectedEvent.id}`].icon}@2x.png`}
                    alt={eventWeather[`event-${selectedEvent.id}`].description}
                    className="appointment-detail__weather-icon"
                  />
                  <div className="appointment-detail__weather-info">
                    <p className="appointment-detail__text">
                      <strong>{eventWeather[`event-${selectedEvent.id}`].temp}°F</strong> - {eventWeather[`event-${selectedEvent.id}`].description}
                    </p>
                    <p className="appointment-detail__text">
                      Precipitation: {eventWeather[`event-${selectedEvent.id}`].precipitation}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedEvent.location_address && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Location</h3>
                <p className="appointment-detail__text">{selectedEvent.location_address}</p>
                {selectedEvent.location_city && (
                  <p className="appointment-detail__text">
                    {selectedEvent.location_city}{selectedEvent.location_state && `, ${selectedEvent.location_state}`} {selectedEvent.location_zip}
                  </p>
                )}
              </div>
            )}

            {selectedEvent.description && (
              <div className="appointment-detail__section">
                <h3 className="appointment-detail__heading">Description</h3>
                <p className="appointment-detail__text" style={{ whiteSpace: 'pre-wrap' }}>{selectedEvent.description}</p>
              </div>
            )}

            <div className="modal__footer">
              <button className="btn btn--danger" onClick={() => handleDeleteEvent(selectedEvent.id)}>
                Delete
              </button>
              <button className="btn btn--outline" onClick={() => handleEditEvent(selectedEvent)}>
                Edit Event
              </button>
              <button className="btn btn--secondary" onClick={() => setShowEventDetailModal(false)}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Conflict Confirmation Modal */}
      <Modal
        isOpen={showConflictModal}
        onClose={handleCancelConflict}
        title="Schedule Conflict"
      >
        <div className="conflict-warning">
          <p className="conflict-warning__message">
            This appointment may conflict with existing appointments:
          </p>
          <ul className="conflict-warning__list">
            {conflictingAppointments.map((apt) => (
              <li key={apt.id} className="conflict-warning__item">
                <strong>{apt.first_name} {apt.last_name}</strong>
                {apt.location_city && ` - ${apt.location_city}`}
              </li>
            ))}
          </ul>
          <p className="conflict-warning__question">
            Are you sure you want to schedule it?
          </p>
          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={handleCancelConflict}
            >
              No
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleConfirmConflict}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Appointments;
